import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "../../../components/layout/Navbar";
import { Footer } from "../../../components/layout/Footer";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Users, Briefcase, Shield } from "lucide-react";

const Auth = () => {
  const navigate = useNavigate();
  const [userType, setUserType] = useState<"investor" | "project-holder" | "admin" | null>(null);

  const handleUserTypeSelect = (type: "investor" | "project-holder" | "admin") => {
    setUserType(type);
  };

  const handleContinue = () => {
    if (userType === "investor") {
      navigate("/investor-register");
    } else if (userType === "project-holder") {
      navigate("/project-holder-register");
    } else if (userType === "admin") {
      navigate("/login");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Get Started</h1>
          <p className="text-muted-foreground text-lg">
            Join our investment platform as an investor, project holder, or administrator
          </p>
        </div>

        {!userType ? (
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <Card
              className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-primary"
              onClick={() => handleUserTypeSelect("investor")}
            >
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                </div>
                <CardTitle>Investor</CardTitle>
                <CardDescription>
                  Invest in promising projects
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Discover and invest in high-potential projects across various industries.
                </p>
              </CardContent>
            </Card>

            <Card
              className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-primary"
              onClick={() => handleUserTypeSelect("project-holder")}
            >
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                  <Briefcase className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <CardTitle>Project Holder</CardTitle>
                <CardDescription>
                  Raise funds for your project
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Present your innovative project to potential investors and secure funding.
                </p>
              </CardContent>
            </Card>

            <Card
              className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-primary"
              onClick={() => handleUserTypeSelect("admin")}
            >
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mb-4">
                  <Shield className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                </div>
                <CardTitle>Administrator</CardTitle>
                <CardDescription>
                  Manage the platform
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Access the admin dashboard to manage users, projects, and platform settings.
                </p>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="max-w-md mx-auto">
            <Card>
              <CardHeader>
                <CardTitle>
                  {userType === "investor" && "Investor Registration"}
                  {userType === "project-holder" && "Project Holder Registration"}
                  {userType === "admin" && "Administrator Login"}
                </CardTitle>
                <CardDescription>
                  {userType === "investor" && "Register as an investor to start investing in projects"}
                  {userType === "project-holder" && "Register as a project holder to raise funds"}
                  {userType === "admin" && "Login to the admin dashboard"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={handleContinue}
                  className="w-full"
                  size="lg"
                >
                  Continue
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setUserType(null)}
                  className="w-full"
                >
                  Back
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="text-center mt-12">
          <p className="text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-primary hover:underline">
              Sign in here
            </Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Auth;
